import { useEffect, useRef } from 'react';
import { unlockScannerAudio } from './scannerAudio';
import { keyEventToUsAscii } from './keyEventToUsAscii';

const MAX_GAP_MS = 80;
const MIN_CODE_LENGTH = 4;
const MAX_CODE_LENGTH = 2048;

const isTextField = (element) => {
    if (!element || element === document.body) return false;
    const tag = element.tagName;
    if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
    if (tag === 'INPUT') {
        const type = String(element.type || 'text').toLowerCase();
        return !['button', 'submit', 'checkbox', 'radio', 'file', 'color', 'range'].includes(
            type
        );
    }
    return Boolean(element.isContentEditable);
};

const stripLeakedScan = (element, code) => {
    if (!isTextField(element) || typeof element.value !== 'string') return;
    const value = element.value;
    if (value.endsWith(code)) {
        element.value = value.slice(0, -code.length);
    } else {
        for (let size = Math.min(4, code.length); size >= 1; size -= 1) {
            const prefix = code.slice(0, size);
            if (value.endsWith(prefix)) {
                element.value = value.slice(0, -size);
                break;
            }
        }
    }
    element.dispatchEvent(new Event('input', { bubbles: true }));
};

export const useKeyboardWedgeScanner = (onScan, enabled = true) => {
    const onScanRef = useRef(onScan);
    onScanRef.current = onScan;

    useEffect(() => {
        if (!enabled) return undefined;

        let buffer = '';
        let leaked = '';
        let lastAt = 0;
        let idleTimer = 0;
        let lastTarget = null;

        const reset = () => {
            window.clearTimeout(idleTimer);
            buffer = '';
            leaked = '';
            lastAt = 0;
            lastTarget = null;
        };

        const submitBuffer = (target) => {
            if (buffer.length < MIN_CODE_LENGTH) {
                reset();
                return;
            }
            const code = buffer;
            stripLeakedScan(target || lastTarget, leaked || code);
            reset();
            void onScanRef.current?.(code);
        };

        const onKeyDown = (event) => {
            if (event.isComposing || event.repeat) return;
            if (event.ctrlKey || event.metaKey || event.altKey) return;

            unlockScannerAudio();

            const now = Date.now();
            if (lastAt && now - lastAt > MAX_GAP_MS) {
                reset();
            }

            if (
                event.key === 'Enter' ||
                event.key === 'Tab' ||
                event.code === 'Enter' ||
                event.code === 'NumpadEnter' ||
                event.code === 'Tab'
            ) {
                if (buffer.length >= MIN_CODE_LENGTH) {
                    event.preventDefault();
                    event.stopPropagation();
                    submitBuffer(event.target);
                } else {
                    reset();
                }
                return;
            }

            const character = keyEventToUsAscii(event);
            if (!character) return;

            if (buffer.length >= 2) {
                event.preventDefault();
                event.stopPropagation();
            } else if (event.key.length === 1) {
                leaked += event.key;
            }

            buffer += character;
            lastAt = now;
            lastTarget = event.target;
            if (buffer.length > MAX_CODE_LENGTH) {
                reset();
                return;
            }
            window.clearTimeout(idleTimer);
            idleTimer = window.setTimeout(() => submitBuffer(event.target), 150);
        };

        window.addEventListener('keydown', onKeyDown, true);
        return () => {
            window.clearTimeout(idleTimer);
            window.removeEventListener('keydown', onKeyDown, true);
        };
    }, [enabled]);
};
